import React from 'react';
import { DocFile, IFileBatch } from '../../sp-types';
import { Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import { ExpandMore, SafetyCheckSharp, TaskAlt } from '@mui/icons-material';
import { v4 } from 'uuid';
import _ from 'lodash';
import { TextDesc } from '../../sp-firebase/docTypesFB';

interface IDocType {
  id: string;
  name: string;
  desc?: string;
  hasFiles: boolean;
}
export const SideMenu: React.FC<{
  batches: IFileBatch[];
  selectedBatch?: string;
  onSelectBatch: (_batch: string) => void;
  docTypes: IDocType[];
  selectedType: string;
  onSelectDocType: (_type: string) => void;
  onCreateBatch: () => void;
}> = ({ batches, selectedBatch, onSelectBatch, docTypes, selectedType, onSelectDocType, onCreateBatch }) => {
  const getFiles = (batchId: string, allBatches: IFileBatch[], selType: string): DocFile[] =>
    allBatches.find((b) => b.id === batchId)?.files?.[selType] || [];

  return (
    <aside className="flex flex-col w-64 bg-white h-full  shadow-custom">
      <div className="p-3 border-b border-gray-200 font-semibold">
        <h4>Document batches</h4>
        <p className="text-sm text-gray-400">create or select existing batch</p>
      </div>
      {!batches?.find((a) => a.status === 'draft') && (
        <div
          className="m-3 p-2 font-medium cursor-pointer rounded border-1 border-blue-600 text-blue-600 text-center"
          onClick={onCreateBatch}
        >
          Create new batch
        </div>
      )}
      <ul className="text-sm">
        {batches?.map(({ id, name, createdAt, status }, index) => (
          <Accordion key={index} elevation={0} square expanded={id === selectedBatch}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              onClick={() => onSelectBatch(id || '')}
              className={`cursor-pointer hover:bg-gray-200 ${
                id === selectedBatch ? 'border-l-8 border-l-red font-medium' : ''
              }`}
              sx={{ borderLeft: '10px red' }}
            >
              <div className="flex flex-1 justify-between items-center">
                {status === 'draft' ? 'New batch' : createdAt?.toDate().toLocaleDateString('en-CA')}
                <StatusChip status={status} />
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <ul className="pl-2">
                {docTypes.map((doc, key) => (
                  <li
                    key={key}
                    className={`p-2 cursor-pointer hover:bg-gray-100 rounded flex flex-col justify-between items-start ${
                      doc.id === selectedType ? 'ring-1 ring-red-200 border-l-4 border-red-200' : ''
                    }`}
                    onClick={() => onSelectDocType(doc.id)}
                  >
                    <div className="flex w-full justify-between ">
                      <p>{doc.name}</p>
                      {getFiles(id, batches, doc.id).length > 0 && <TaskAlt className="text-green-300 h-6" />}
                    </div>
                    <div className="flex flex-1 text-sm text-gray-400">{doc.desc}</div>
                  </li>
                ))}
              </ul>
            </AccordionDetails>
          </Accordion>
        ))}
      </ul>
    </aside>
  );
};

export const DocumentTypes: React.FC<{ docTypes: IDocType[] }> = ({ docTypes }) => (
  <div className="flex flex-col gap-2 justify-between">
    <p className="font-bold py-1 h-10">Document type</p>
    <div className="flex flex-col  border-gray-110 rounded w-80 p-2 h-[455px]">
      <ul className="flex flex-col gap-3 h-full">
        {docTypes.map(({ id, name, hasFiles }) => (
          <li key={id} className="bg-blue-100 px-3 py-1 rounded-full text-sm">
            {name}
            {hasFiles && <p>V</p>}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const okStats = ['pending', 'completed'];
export const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <p
    className={`rounded-full p-2 text-center border-1 ${
      okStats.includes(status) ? 'text-green-300 border-green-300' : 'text-blue-300 border-blue-300'
    }`}
  >
    {status}
  </p>
);

export const allDocTypes: TextDesc[] = [
  { text: 'Sales expectations' },
  { text: 'Inventory' },
  { text: 'Financial position' },
  { text: 'General' },
];

export const mapDocType = ({ text, desc }: TextDesc): IDocType => ({
  id: text.replace(' ', '_').toLowerCase(),
  name: text,
  desc,
  hasFiles: false,
});

export const updateBatchFiles = (
  { files, id, name, ...batch }: IFileBatch,
  addedPaths: string[],
  addedUrls: string[],
  docType: string,
): IFileBatch => ({
  ...batch,
  id: id === 'new' ? v4().slice(0, 6) : id,
  files: {
    ...files,
    [docType]: [
      ...(files[docType] || []),
      ...addedPaths.map((path, index) => ({
        id: index + (files[docType]?.length || 0),
        path,
        url: addedUrls[index],
      })),
    ],
  },
});

export const deleteFile = ({ files, ...batch }: IFileBatch, fileIndex: number, docType: string): IFileBatch => ({
  ...batch,
  files: {
    ...files,
    [docType]: files[docType]?.filter((_, i) => i !== fileIndex),
  },
});
